import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { formatDisplayDate } from "@/lib/dateFormat";
import AdminLayout from "@/components/AdminLayout";
import Banner from "@/components/Banner";
import { useAuth } from "@/context/AuthContext";
import { hasAction } from "@/lib/rbac";
import { SortableTH, useSortableData, EmptyState } from "@/components/SortableTable";
import TableSkeleton from "@/components/TableSkeleton";
import RowActionsMenu from "@/components/RowActionsMenu";
import ColumnFilterRow, { applyColumnFilters, columnFiltersActive } from "@/components/ColumnFilterRow";
import CandidateFormModal from "@/components/CandidateFormModal";
import CandidateHireModal from "@/components/CandidateHireModal";
import {
  Search,
  RefreshCw,
  Columns3,
  Filter,
  GripVertical,
  RotateCcw,
  X,
  Eye,
  Pencil,
  Trash2,
  UserCheck,
  FileSignature,
  UserPlus,
  Users,
  CheckCircle2,
  FileText,
  PauseCircle,
} from "lucide-react";

const STATUSES = ["Draft", "Offered", "Accepted", "Hired", "Rejected", "Withdrawn", "No-show"];
const OPEN_STATUSES = new Set(["Draft", "Offered", "Accepted"]);
const HIRE_TYPES = ["Intern", "Full-time", "Part-time", "Contract"];

const STATUS_COLORS = {
  Draft: "bg-slate-100 text-slate-700 border-slate-200",
  Offered: "bg-sky-100 text-sky-800 border-sky-200",
  Accepted: "bg-emerald-100 text-emerald-800 border-emerald-200",
  Hired: "bg-[#E8F0FF] text-[#226DB4] border-[#226DB4]/30",
  Rejected: "bg-rose-100 text-rose-700 border-rose-200",
  Withdrawn: "bg-amber-100 text-amber-800 border-amber-200",
  "No-show": "bg-orange-100 text-orange-800 border-orange-200",
};

const COLUMN_DEFINITIONS = [
  { key: "code", label: "Code", defaultVisible: true, width: "min-w-[90px]" },
  { key: "full_name", label: "Name", defaultVisible: true, width: "min-w-[180px]" },
  { key: "email", label: "Email", defaultVisible: true, width: "min-w-[200px]" },
  { key: "phone", label: "Phone", defaultVisible: false, width: "min-w-[120px]" },
  { key: "employment_type", label: "Type", defaultVisible: true, width: "min-w-[110px]" },
  { key: "department", label: "Department", defaultVisible: true, width: "min-w-[140px]" },
  { key: "designation", label: "Designation", defaultVisible: true, width: "min-w-[140px]" },
  { key: "joining_date", label: "Joining Date", defaultVisible: true, width: "min-w-[120px]" },
  { key: "status", label: "Status", defaultVisible: true, width: "min-w-[100px]" },
  { key: "offer_date", label: "Offer Date", defaultVisible: false, width: "min-w-[120px]" },
  { key: "ctc", label: "CTC / Stipend", defaultVisible: false, width: "min-w-[120px]" },
  { key: "city", label: "City", defaultVisible: false, width: "min-w-[100px]" },
  { key: "gender", label: "Gender", defaultVisible: false, width: "min-w-[90px]" },
];
const COLUMN_BY_KEY = Object.fromEntries(COLUMN_DEFINITIONS.map((c) => [c.key, c]));
const LS_COL_ORDER = "hrms.candidates.colOrder.v1";
const LS_COL_VISIBLE = "hrms.candidates.colVisible.v1";

function displayName(c) {
  return [c.first_name, c.middle_name, c.last_name].filter(Boolean).join(" ");
}

const fieldCls =
  "w-full border border-slate-200 rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#226DB4]/30 focus:border-[#226DB4]";

export default function Candidates() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const canAdd = hasAction(user, "candidates", "add") || user?.role === "admin";
  const canEdit = hasAction(user, "candidates", "edit") || user?.role === "admin";
  const canDelete = hasAction(user, "candidates", "delete") || user?.role === "admin";
  const canLetters = hasAction(user, "letters", "add") || hasAction(user, "letters", "view") || user?.role === "admin";

  const [items, setItems] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [locations, setLocations] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [companyFilter, setCompanyFilter] = useState(() => {
    try { return localStorage.getItem("hrms_company_filter") || "all"; } catch { return "all"; }
  });
  const [showFilters, setShowFilters] = useState(false);
  const [joiningFrom, setJoiningFrom] = useState("");
  const [joiningTo, setJoiningTo] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [colFilters, setColFilters] = useState({});
  const [colsOpen, setColsOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [hireTarget, setHireTarget] = useState(null);

  const loadStoredOrder = () => {
    try {
      const v = JSON.parse(localStorage.getItem(LS_COL_ORDER) || "null");
      if (Array.isArray(v) && v.length) {
        const filtered = v.filter((k) => COLUMN_BY_KEY[k]);
        for (const c of COLUMN_DEFINITIONS) {
          if (!filtered.includes(c.key)) filtered.push(c.key);
        }
        return filtered;
      }
    } catch { /* ignore */ }
    return COLUMN_DEFINITIONS.map((c) => c.key);
  };
  const loadStoredVisible = () => {
    try {
      const v = JSON.parse(localStorage.getItem(LS_COL_VISIBLE) || "null");
      if (v && typeof v === "object") {
        return Object.fromEntries(
          COLUMN_DEFINITIONS.map((c) => [c.key, c.key in v ? !!v[c.key] : c.defaultVisible]),
        );
      }
    } catch { /* ignore */ }
    return Object.fromEntries(COLUMN_DEFINITIONS.map((c) => [c.key, c.defaultVisible]));
  };

  const [colOrder, setColOrder] = useState(loadStoredOrder);
  const [colVisible, setColVisible] = useState(loadStoredVisible);
  useEffect(() => { localStorage.setItem(LS_COL_ORDER, JSON.stringify(colOrder)); }, [colOrder]);
  useEffect(() => { localStorage.setItem(LS_COL_VISIBLE, JSON.stringify(colVisible)); }, [colVisible]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const [cands, deps, desigs, cos, emps, locs] = await Promise.all([
        api.get("/candidates"),
        api.get("/departments"),
        api.get("/designations"),
        api.get("/companies").catch(() => ({ data: [] })),
        api.get("/employees").catch(() => ({ data: [] })),
        api.get("/locations").catch(() => ({ data: [] })),
      ]);
      setItems(cands.data || []);
      setDepartments(deps.data || []);
      setDesignations(desigs.data || []);
      setCompanies(cos.data || []);
      setEmployees((emps.data || []).filter((e) => e.employment?.status !== "Inactive"));
      setLocations((locs.data || []).filter((l) => l.is_active !== false));
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load candidates");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  /** Keep ?action=add in the URL while the create form is open so the sidebar stays on "Add Candidate". */
  const clearFormQueryParams = useCallback(() => {
    if (!searchParams.get("action") && !searchParams.get("edit") && !searchParams.get("hire")) return;
    const next = new URLSearchParams(searchParams);
    next.delete("action");
    next.delete("edit");
    next.delete("hire");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (searchParams.get("action") === "add" && canAdd && !formOpen) {
      setEditing(null);
      setFormOpen(true);
      // Keep ?action=add until the modal closes (sidebar active state).
    }
    const editId = searchParams.get("edit");
    if (editId && items.length && !formOpen) {
      const t = items.find((x) => x.id === editId);
      if (t) {
        setEditing(t);
        setFormOpen(true);
        // Keep ?edit= while the form is open.
      }
    }
    const hireId = searchParams.get("hire");
    if (hireId && items.length && !hireTarget) {
      const t = items.find((x) => x.id === hireId);
      if (t) {
        setHireTarget(t);
        // Keep ?hire= until the hire modal closes.
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, items.length]);

  useEffect(() => {
    try { localStorage.setItem("hrms_company_filter", companyFilter); } catch { /* ignore */ }
  }, [companyFilter]);

  const depMap = useMemo(() => Object.fromEntries(departments.map((d) => [d.id, d])), [departments]);
  const desigMap = useMemo(() => Object.fromEntries(designations.map((d) => [d.id, d])), [designations]);

  const stats = useMemo(() => {
    const open = items.filter((c) => OPEN_STATUSES.has(c.status)).length;
    const offered = items.filter((c) => c.status === "Offered" || c.status === "Accepted").length;
    const hired = items.filter((c) => c.status === "Hired").length;
    return { total: items.length, open, offered, hired };
  }, [items]);

  const candColValue = (c, key) => {
    switch (key) {
      case "code": return c.code || "";
      case "full_name": return displayName(c);
      case "email": return c.email || "";
      case "phone": return c.phone || "";
      case "employment_type": return c.intended_employment_type || "";
      case "department": return depMap[c.department_id]?.name || "";
      case "designation": return desigMap[c.designation_id]?.title || "";
      case "joining_date": return c.proposed_joining_date || "";
      case "status": return c.status || "";
      case "offer_date": return c.offer_date || "";
      case "ctc": return String(c.annual_ctc || 0);
      case "city": return c.city || "";
      case "gender": return c.gender || "";
      default: return "";
    }
  };

  const filtered = useMemo(() => {
    let rows = items;
    if (companyFilter && companyFilter !== "all") {
      rows = rows.filter((c) => (c.company_id || "company") === companyFilter);
    }
    if (statusFilter && statusFilter !== "All") {
      rows = rows.filter((c) => c.status === statusFilter);
    }
    if (typeFilter) {
      rows = rows.filter((c) => c.intended_employment_type === typeFilter);
    }
    if (joiningFrom) {
      rows = rows.filter((c) => (c.proposed_joining_date || "") >= joiningFrom);
    }
    if (joiningTo) {
      rows = rows.filter((c) => (c.proposed_joining_date || "") <= joiningTo);
    }
    const s = search.trim().toLowerCase();
    if (s) {
      rows = rows.filter((c) => {
        const blob = [displayName(c), c.code, c.email, c.phone].join(" ").toLowerCase();
        return blob.includes(s);
      });
    }
    const colTypes = {
      status: "select",
      employment_type: "select",
      gender: "select",
      department: "select",
      joining_date: "date",
      offer_date: "date",
      ctc: "number",
    };
    rows = applyColumnFilters(rows, colFilters, (row, key) => candColValue(row, key), colTypes);
    return rows;
  }, [items, companyFilter, statusFilter, typeFilter, joiningFrom, joiningTo, search, colFilters, depMap, desigMap]);

  const sortAccessors = useMemo(() => ({
    code: (c) => c.code || "",
    full_name: (c) => displayName(c),
    email: (c) => c.email || "",
    phone: (c) => c.phone || "",
    employment_type: (c) => c.intended_employment_type || "",
    department: (c) => depMap[c.department_id]?.name || "",
    designation: (c) => desigMap[c.designation_id]?.title || "",
    joining_date: (c) => c.proposed_joining_date || "",
    status: (c) => c.status || "",
    offer_date: (c) => c.offer_date || "",
    ctc: (c) => Number(c.annual_ctc || 0),
    city: (c) => c.city || "",
    gender: (c) => c.gender || "",
  }), [depMap, desigMap]);

  const { items: sortedFiltered, sortBy, sortDir, requestSort } = useSortableData(filtered, {
    initialKey: "code",
    accessors: sortAccessors,
  });

  const visibleCols = useMemo(
    () => colOrder.filter((k) => colVisible[k]).map((k) => COLUMN_BY_KEY[k]).filter(Boolean),
    [colOrder, colVisible],
  );

  const filterCols = useMemo(() => visibleCols.map((c) => {
    if (c.key === "status") return { key: c.key, filterType: "select", options: STATUSES };
    if (c.key === "employment_type") return { key: c.key, filterType: "select", options: HIRE_TYPES };
    if (c.key === "gender") return { key: c.key, filterType: "select", options: ["Male", "Female", "Other"] };
    if (c.key === "department") return { key: c.key, filterType: "select", options: departments.map((d) => d.name) };
    if (c.key === "joining_date" || c.key === "offer_date") return { key: c.key, filterType: "date" };
    if (c.key === "ctc") return { key: c.key, filterType: "number" };
    return { key: c.key, filterType: "text" };
  }), [visibleCols, departments]);

  const activeFilterCount = [joiningFrom, joiningTo, typeFilter].filter(Boolean).length
    + (columnFiltersActive(colFilters) ? 1 : 0);

  const clearFilters = () => {
    setJoiningFrom("");
    setJoiningTo("");
    setTypeFilter("");
    setColFilters({});
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete candidate ${displayName(c)} (${c.code})?`)) return;
    try {
      await api.delete(`/candidates/${c.id}`);
      setOkMsg("Candidate deleted");
      await loadAll();
      setTimeout(() => setOkMsg(""), 3000);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <AdminLayout title="Candidates" breadcrumbs={["Home", "Pre-hire", "Candidates"]} accent="prehire">
      <Banner variant="error" onClose={() => setErr("")}>{err}</Banner>
      <Banner variant="success" onClose={() => setOkMsg("")}>{okMsg}</Banner>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Total Candidates" value={stats.total} color="#0284c7" icon={<Users className="h-6 w-6" />} />
        <StatCard label="Open Pipeline" value={stats.open} color="#0ea5e9" icon={<UserPlus className="h-6 w-6" />} />
        <StatCard label="Offered / Accepted" value={stats.offered} color="#089949" icon={<CheckCircle2 className="h-6 w-6" />} />
        <StatCard label="Hired" value={stats.hired} color="#64748b" icon={<PauseCircle className="h-6 w-6" />} />
      </div>

      <div className="bg-white border border-sky-200/70 rounded shadow-sm">
        <div className="px-5 py-3.5 border-b border-sky-100 bg-sky-50/40 flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-base font-semibold text-slate-800">Candidate List</h3>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                data-testid="candidate-search-input"
                placeholder="Search name, code, email…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 pr-3 py-2 text-sm border border-slate-200 rounded w-64 focus:outline-none focus:ring-2 focus:ring-[#226DB4]/30 focus:border-[#226DB4]"
              />
            </div>
            <select
              value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
              className="px-3 py-2 text-sm border border-slate-200 rounded bg-white max-w-[200px]"
            >
              <option value="all">All Companies</option>
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <select
              data-testid="candidate-status-filter"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setColFilters((prev) => (prev.status ? { ...prev, status: "" } : prev));
              }}
              className="px-3 py-2 text-sm border border-slate-200 rounded bg-white"
            >
              <option value="All">All Statuses</option>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>

            <button
              data-testid="filters-toggle-btn"
              onClick={() => setShowFilters(!showFilters)}
              className={`inline-flex items-center gap-1 px-3 py-2 text-sm border rounded ${
                activeFilterCount > 0
                  ? "border-[#226DB4] bg-[#226DB4]/10 text-[#226DB4]"
                  : "border-slate-200 text-slate-600 hover:bg-slate-50"
              }`}
            >
              <Filter className="h-4 w-4" /> Filters
              {activeFilterCount > 0 && (
                <span className="bg-[#226DB4] text-white text-[10px] rounded-full px-1.5 ml-0.5">{activeFilterCount}</span>
              )}
            </button>
            <button
              data-testid="columns-btn"
              onClick={() => setColsOpen(true)}
              className="inline-flex items-center gap-1 px-3 py-2 text-sm border border-slate-200 rounded text-slate-600 hover:bg-slate-50"
            >
              <Columns3 className="h-4 w-4" /> Columns
            </button>
            <button
              data-testid="refresh-btn"
              onClick={loadAll}
              className="inline-flex items-center gap-1 px-3 py-2 text-sm border border-slate-200 rounded text-slate-600 hover:bg-slate-50"
              title="Refresh"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="px-5 py-4 bg-sky-50/50 border-b border-sky-100 space-y-3" data-testid="filters-panel">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
              <div>
                <label className="block text-[11px] font-semibold text-slate-600 uppercase tracking-wide mb-1.5">Joining From</label>
                <input type="date" value={joiningFrom} onChange={(e) => setJoiningFrom(e.target.value)} className={fieldCls} />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-600 uppercase tracking-wide mb-1.5">Joining To</label>
                <input type="date" value={joiningTo} onChange={(e) => setJoiningTo(e.target.value)} className={fieldCls} />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-600 uppercase tracking-wide mb-1.5">Employment Type</label>
                <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className={fieldCls}>
                  <option value="">All types</option>
                  {HIRE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
            <div className="flex justify-between">
              <div className="text-[11px] text-slate-500">
                {activeFilterCount > 0 ? `${activeFilterCount} filter(s) applied` : "No advanced filters"}
              </div>
              <button
                type="button"
                onClick={clearFilters}
                disabled={activeFilterCount === 0}
                className="px-3 py-1.5 text-xs border rounded disabled:opacity-50"
              >
                Clear filters
              </button>
            </div>
          </div>
        )}

        <div className="overflow-x-auto" data-testid="candidates-table">
          <table className="w-full text-sm">
            <thead className="bg-sky-50">
              <tr>
                {visibleCols.map((c) => (
                  sortAccessors[c.key] ? (
                    <SortableTH key={c.key} columnKey={c.key} active={sortBy} dir={sortDir} onClick={requestSort} className={c.width}>
                      {c.label}
                    </SortableTH>
                  ) : (
                    <th key={c.key} className={`text-left px-4 py-3 text-xs uppercase tracking-wider text-slate-600 font-semibold ${c.width}`}>{c.label}</th>
                  )
                ))}
                <th className="text-right px-4 py-3 text-xs uppercase tracking-wider text-slate-600 font-semibold min-w-[120px]">Actions</th>
              </tr>
              <ColumnFilterRow
                columns={filterCols}
                filters={colFilters}
                onChange={(key, val) => setColFilters((prev) => ({ ...prev, [key]: val }))}
                trailingCells={1}
              />
            </thead>
            <tbody>
              {loading ? (
                <TableSkeleton rows={6} cols={visibleCols.length + 1} />
              ) : sortedFiltered.length === 0 ? (
                <EmptyState colSpan={visibleCols.length + 1} message="No candidates match the current filters" />
              ) : (
                sortedFiltered.map((c) => (
                  <tr
                    key={c.id}
                    className="border-t border-slate-100 hover:bg-slate-50/80 cursor-pointer"
                    data-testid={`candidate-row-${c.code}`}
                    onClick={() => navigate(`/candidates/${c.id}`)}
                  >
                    {visibleCols.map((col) => (
                      <td key={col.key} className="px-4 py-3 text-slate-700">
                        {col.key === "status" ? (
                          <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-medium border ${STATUS_COLORS[c.status] || "bg-slate-100"}`}>
                            {c.status}
                          </span>
                        ) : col.key === "full_name" ? (
                          <div>
                            <div className="font-medium text-slate-800">{displayName(c)}</div>
                            <div className="text-[11px] text-slate-400 sm:hidden">{c.email}</div>
                          </div>
                        ) : col.key === "ctc" ? (
                          c.annual_ctc ? `₹ ${Number(c.annual_ctc).toLocaleString("en-IN")}` : "—"
                        ) : col.key === "joining_date" || col.key === "offer_date" ? (
                          formatDisplayDate(candColValue(c, col.key))
                        ) : (
                          candColValue(c, col.key) || "—"
                        )}
                      </td>
                    ))}
                    <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      <RowActionsMenu
                        items={[
                          { label: "View Profile", icon: Eye, onClick: () => navigate(`/candidates/${c.id}`), testid: `view-cand-${c.code}` },
                          { label: "Edit", icon: Pencil, onClick: () => { setEditing(c); setFormOpen(true); }, hidden: !canEdit || c.status === "Hired", testid: `edit-cand-${c.code}` },
                          { label: "Issue Offer", icon: FileSignature, onClick: () => navigate(`/letters?action=generate&candidate_id=${c.id}`), hidden: !canLetters || !OPEN_STATUSES.has(c.status), testid: `offer-cand-${c.code}` },
                          { label: "Hire", icon: UserCheck, onClick: () => setHireTarget(c), hidden: !canEdit || !OPEN_STATUSES.has(c.status), testid: `hire-cand-${c.code}` },
                          { label: "Open Employee", icon: FileText, onClick: () => navigate(`/employees/${c.hired_employee_id}`), hidden: !c.hired_employee_id },
                          { label: "Delete", icon: Trash2, danger: true, onClick: () => remove(c), hidden: !canDelete || c.status === "Hired", testid: `delete-cand-${c.code}` },
                        ]}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="px-5 py-3 text-xs text-slate-500 border-t border-slate-200 flex items-center gap-3 flex-wrap">
          <span>
            Showing <span className="font-semibold">{filtered.length}</span> of <span className="font-semibold">{items.length}</span> candidates
          </span>
          {columnFiltersActive(colFilters) && (
            <button type="button" onClick={() => setColFilters({})} className="text-xs text-[#226DB4] hover:underline">
              Clear column filters
            </button>
          )}
        </div>
      </div>

      {formOpen && (
        <CandidateFormModal
          editing={editing}
          companies={companies}
          departments={departments}
          designations={designations}
          locations={locations}
          onClose={() => {
            setFormOpen(false);
            setEditing(null);
            clearFormQueryParams();
          }}
          onSaved={async (saved) => {
            const wasEdit = Boolean(editing);
            setFormOpen(false);
            setEditing(null);
            clearFormQueryParams();
            setOkMsg(wasEdit ? "Candidate updated" : "Candidate created");
            await loadAll();
            if (saved?.id && !wasEdit) navigate(`/candidates/${saved.id}`);
            setTimeout(() => setOkMsg(""), 3000);
          }}
        />
      )}

      {hireTarget && (
        <CandidateHireModal
          candidate={hireTarget}
          departments={departments}
          designations={designations}
          employees={employees}
          onClose={() => {
            setHireTarget(null);
            clearFormQueryParams();
          }}
          onDone={async (res) => {
            setHireTarget(null);
            clearFormQueryParams();
            setOkMsg(res?.message || "Hired");
            await loadAll();
            if (res?.employee_id) navigate(`/employees/${res.employee_id}`);
            setTimeout(() => setOkMsg(""), 4000);
          }}
        />
      )}

      {colsOpen && (
        <ColumnsPanel
          colOrder={colOrder}
          setColOrder={setColOrder}
          colVisible={colVisible}
          setColVisible={setColVisible}
          onReset={() => {
            setColOrder(COLUMN_DEFINITIONS.map((c) => c.key));
            setColVisible(Object.fromEntries(COLUMN_DEFINITIONS.map((c) => [c.key, c.defaultVisible])));
          }}
          onClose={() => setColsOpen(false)}
        />
      )}
    </AdminLayout>
  );
}

function StatCard({ label, value, color, icon }) {
  return (
    <div className="bg-white border border-slate-200 rounded shadow-sm overflow-hidden flex items-stretch">
      <div className="flex items-center justify-center text-white px-5" style={{ backgroundColor: color }}>{icon}</div>
      <div className="px-4 py-3 flex-1">
        <div className="text-2xl font-bold text-slate-800 leading-none">{value}</div>
        <div className="text-xs text-slate-500 mt-1 uppercase tracking-wider font-medium">{label}</div>
      </div>
    </div>
  );
}

function ColumnsPanel({ colOrder, setColOrder, colVisible, setColVisible, onReset, onClose }) {
  const dragKey = useRef(null);
  const handleDragStart = (k) => () => { dragKey.current = k; };
  const handleDragOver = (k) => (e) => {
    e.preventDefault();
    if (!dragKey.current || dragKey.current === k) return;
    const src = dragKey.current;
    setColOrder((prev) => {
      const next = prev.filter((x) => x !== src);
      next.splice(next.indexOf(k), 0, src);
      return next;
    });
  };
  const visibleCount = colOrder.filter((k) => colVisible[k]).length;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[85vh] flex flex-col" data-testid="columns-panel">
        <div className="px-5 py-3.5 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
              <Columns3 className="h-4 w-4 text-[#226DB4]" /> Configure Columns
            </h3>
            <p className="text-[11px] text-slate-500 mt-0.5">{visibleCount} of {colOrder.length} visible · drag to reorder</p>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 text-slate-400 hover:bg-slate-100 rounded"><X className="h-4 w-4" /></button>
        </div>
        <ul className="flex-1 overflow-y-auto p-2">
          {colOrder.map((key) => {
            const c = COLUMN_BY_KEY[key];
            if (!c) return null;
            const isOn = !!colVisible[key];
            return (
              <li
                key={key}
                draggable
                onDragStart={handleDragStart(key)}
                onDragOver={handleDragOver(key)}
                onDragEnd={() => { dragKey.current = null; }}
                className="flex items-center gap-3 px-3 py-2 rounded hover:bg-slate-50 border border-transparent hover:border-slate-200 cursor-move"
              >
                <GripVertical className="h-4 w-4 text-slate-400 shrink-0" />
                <input type="checkbox" checked={isOn} onChange={() => setColVisible((v) => ({ ...v, [key]: !v[key] }))} className="rounded" />
                <span className={`flex-1 text-sm ${isOn ? "text-slate-800" : "text-slate-400"}`}>{c.label}</span>
              </li>
            );
          })}
        </ul>
        <div className="px-4 py-3 border-t border-slate-200 flex justify-between">
          <button type="button" onClick={onReset} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs border rounded text-slate-600">
            <RotateCcw className="h-3.5 w-3.5" /> Reset
          </button>
          <button type="button" onClick={onClose} className="px-4 py-1.5 text-sm bg-[#226DB4] hover:bg-[#1B5A94] text-white rounded font-medium">Done</button>
        </div>
      </div>
    </div>
  );
}
